/**
 * Key and mouse tables from DESIGN 6.1. Everything keys off
 * `KeyboardEvent.code` (physical position) so non-QWERTY layouts keep the
 * Magicka hand shape: left hand on QWER/ASDF, right hand on the mouse.
 *
 * Pure data and lookups; no DOM access so the tables are testable in node.
 */

import type { BaseElement, Vec2 } from '@shared/index.js';
import { ELEMENT_KEYS, moveDir8 } from '@shared/index.js';

/** Element presses: Q W E R / A S D F -> Water Life Shield Cold / Lightning Arcane Earth Fire. */
export const ELEMENT_CODES: Readonly<Record<string, BaseElement>> = ELEMENT_KEYS;

/** Key cap shown on the HUD for each element (DESIGN 6.5 "the eight element keys"). */
export const ELEMENT_KEY_LABELS: Readonly<Record<BaseElement, string>> = {
  water: 'Q', life: 'W', shield: 'E', cold: 'R', lightning: 'A', arcane: 'S', earth: 'D', fire: 'F',
};

/** Non-element keys of DESIGN 6.1. */
export type ActionKey =
  | 'clear'      // Backspace (Escape opens the menu since 2026-10-28)
  | 'self'       // (middle mouse; no key by default)
  | 'weapon'     // V
  | 'magick'     // Space (G also works)
  | 'spellbook'  // Tab (hold)
  | 'chat'       // Enter
  | 'mute'       // M
  | 'moveUp' | 'moveDown' | 'moveLeft' | 'moveRight'; // arrows

export const ACTION_CODES: Readonly<Record<string, ActionKey>> = {
  Backspace: 'clear',
  Space: 'magick',
  KeyV: 'weapon',
  KeyG: 'magick',
  Tab: 'spellbook',
  Enter: 'chat',
  NumpadEnter: 'chat',
  KeyM: 'mute',
  ArrowUp: 'moveUp',
  ArrowDown: 'moveDown',
  ArrowLeft: 'moveLeft',
  ArrowRight: 'moveRight',
};

export type MoveKey = Extract<ActionKey, 'moveUp' | 'moveDown' | 'moveLeft' | 'moveRight'>;

/** Screen-relative unit directions for the arrow keys (y down, DESIGN 6.1). */
export const MOVE_DIRS: Readonly<Record<MoveKey, Vec2>> = {
  moveUp: { x: 0, y: -1 },
  moveDown: { x: 0, y: 1 },
  moveLeft: { x: -1, y: 0 },
  moveRight: { x: 1, y: 0 },
};

/** Mouse button indexes (MouseEvent.button). */
export const MOUSE = {
  /** LMB: force cast; with the area modifier held, area cast. */
  cast: 0,
  /** MMB: self cast; with the area modifier held, weapon. */
  self: 1,
  /** RMB: walk toward the cursor while held; releasing stops. */
  walk: 2,
} as const;

/**
 * Keys whose browser default (focus change, page scroll, history back) must
 * never fire while the game has focus (DESIGN 6.1 last paragraph).
 */
export const PREVENT_DEFAULT_CODES: ReadonlySet<string> = new Set([
  'Tab', 'Space', 'Backspace', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
]);

export function codeToElement(code: string): BaseElement | null {
  return Object.hasOwn(ELEMENT_CODES, code) ? (ELEMENT_CODES[code] ?? null) : null;
}

export function codeToAction(code: string): ActionKey | null {
  return Object.hasOwn(ACTION_CODES, code) ? (ACTION_CODES[code] ?? null) : null;
}

export function isMoveKey(key: ActionKey): key is MoveKey {
  return key === 'moveUp' || key === 'moveDown' || key === 'moveLeft' || key === 'moveRight';
}

/** Shift turns LMB into an area cast and MMB into a weapon cast (DESIGN 6.1). */
export function isAreaModifier(ev: { shiftKey: boolean }): boolean {
  return ev.shiftKey;
}

/**
 * Combine the held arrow keys into a normalised direction (8 directions);
 * opposite keys cancel. Null when nothing is held or everything cancels.
 */
export function arrowDirection(held: ReadonlySet<MoveKey>): Vec2 | null {
  let x = 0, y = 0;
  for (const k of held) {
    const d = MOVE_DIRS[k];
    x += d.x;
    y += d.y;
  }
  // The shared helper produces the exact floats the server steps with, so the
  // prediction replays the same arithmetic as the simulation (CONVENTIONS).
  const mv = moveDir8(x, y);
  return mv.k === 'd' ? { x: mv.x, y: mv.y } : null;
}
